import { useCallback, useEffect, useRef, useState } from "react";

/**
 * ファイル単位のアンドゥ/リドゥスタック。
 * - グリッドや manifest フォームのような「アトミックな編集」用。
 * - CodeMirror などキーストローク単位の編集は内蔵 undo に任せ、本フックではバルク値の差分のみ追う。
 *
 * 仕様:
 *  - push(file, oldValue) を編集の直前に呼ぶ
 *  - undo(file) は { value, didUndo } を返す。何もなければ didUndo=false
 *  - redo(file) も同様
 *  - resetFile(file, value) は履歴をクリアして初期値を 1 件だけ積み直す
 *  - swap(file, currentValue) は redo 時に「現在値」を redo スタックの上に積むのに使う
 */

interface FileHistory {
  undo: string[];
  redo: string[];
}

const MAX_DEPTH = 80;

export function useEditHistory() {
  const ref = useRef<Map<string, FileHistory>>(new Map());
  // 再レンダ用フラグ
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => n + 1), []);

  const get = (file: string): FileHistory => {
    let h = ref.current.get(file);
    if (!h) {
      h = { undo: [], redo: [] };
      ref.current.set(file, h);
    }
    return h;
  };

  const push = useCallback(
    (file: string, oldValue: string) => {
      const h = get(file);
      h.undo.push(oldValue);
      if (h.undo.length > MAX_DEPTH) h.undo.shift();
      h.redo.length = 0;
      bump();
    },
    [bump],
  );

  const undo = useCallback(
    (file: string, currentValue: string): string | null => {
      const h = get(file);
      const prev = h.undo.pop();
      if (prev === undefined) return null;
      h.redo.push(currentValue);
      bump();
      return prev;
    },
    [bump],
  );

  const redo = useCallback(
    (file: string, currentValue: string): string | null => {
      const h = get(file);
      const next = h.redo.pop();
      if (next === undefined) return null;
      h.undo.push(currentValue);
      bump();
      return next;
    },
    [bump],
  );

  const canUndo = (file: string) => get(file).undo.length > 0;
  const canRedo = (file: string) => get(file).redo.length > 0;

  return { push, undo, redo, canUndo, canRedo };
}

/**
 * (file, value) ペアの履歴フック。
 * 内部で useEditHistory を抱え、Ctrl+Z / Ctrl+Y のグローバルハンドラまで提供する。
 */
export function useFileEditHistory(opts: {
  activeFile: string;
  getValue: (file: string) => string;
  setValue: (file: string, value: string) => void;
}) {
  const { activeFile, getValue, setValue } = opts;
  const history = useEditHistory();

  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const cmd = e.ctrlKey || e.metaKey;
      if (!cmd) return;
      // テキストフィールド内では各 IME 制御に任せる
      const tag = (e.target as HTMLElement | null)?.tagName ?? "";
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const ce = (e.target as HTMLElement | null)?.isContentEditable;
      if (ce) return;
      if (e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        const file = optsRef.current.activeFile;
        const prev = history.undo(file, optsRef.current.getValue(file));
        if (prev !== null) optsRef.current.setValue(file, prev);
      } else if (e.key === "y" || (e.key === "z" && e.shiftKey)) {
        e.preventDefault();
        const file = optsRef.current.activeFile;
        const next = history.redo(file, optsRef.current.getValue(file));
        if (next !== null) optsRef.current.setValue(file, next);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [history]);

  return {
    push: history.push,
    canUndo: () => history.canUndo(activeFile),
    canRedo: () => history.canRedo(activeFile),
    undo: () => {
      const prev = history.undo(activeFile, getValue(activeFile));
      if (prev !== null) setValue(activeFile, prev);
    },
    redo: () => {
      const next = history.redo(activeFile, getValue(activeFile));
      if (next !== null) setValue(activeFile, next);
    },
  };
}
